<template>
  <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid"
    @row-inserted="onRowInserted"
    @row-updated="onRowUpdated"
    @row-removed="onRowRemoved">
    <DxColumnFixing :enabled="true" />

    <DxColumn data-field="IdTipoTramite" :caption="$t('comisiones.grid.tipo_tramite')">
      <DxLookup column-auto-width="true" v-bind="getLookupTipoTramite()"></DxLookup>
    </DxColumn>
    <DxColumn data-field="Importe" :caption="$t('ofertas.grid.importe')" data-type="number"  
      cell-template="importeTemplate">
        <DxRequiredRule />
        <DxRangeRule :min="0" :message="$t('comisiones.grid.importe_validacion')" :reevaluate="true"/>
    </DxColumn>
    <DxColumn data-field="AplicaPotencia" :caption="$t('ofertas.grid.aplica_potencia')"
      cell-template="booleanTemplate">
      <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
    </DxColumn>
    <DxColumn data-field="PotenciaMin" :caption="$t('comisiones.grid.potencia_min')" data-type="number"
      cell-template="potenciaTemplate">
      <DxCustomRule :validation-callback="validatePotenciaMin" :message="$t('comisiones.grid.potencia_validacion')" :reevaluate="true"/>
    </DxColumn>
    <DxColumn data-field="PotenciaMax" :caption="$t('comisiones.grid.potencia_max')" data-type="number"
      cell-template="potenciaTemplate">
      <DxCustomRule :validation-callback="validatePotenciaMax" :message="$t('comisiones.grid.potencia_validacion')" :reevaluate="true"/>
    </DxColumn>
    <DxColumn data-field="AplicaConsumo" :caption="$t('ofertas.grid.aplica_consumo')"
      cell-template="booleanTemplate">
      <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
    </DxColumn>
    <DxColumn data-field="ConsumoMin" :caption="$t('comisiones.grid.consumo_min')" data-type="number"
      cell-template="consumoTemplate">
      <DxCustomRule :validation-callback="validateConsumoMin" :message="$t('comisiones.grid.consumo_validacion')" :reevaluate="true"/>
    </DxColumn>
    <DxColumn data-field="ConsumoMax" :caption="$t('comisiones.grid.consumo_max')" data-type="number"
      cell-template="consumoTemplate">
      <DxCustomRule :validation-callback="validateConsumoMax" :message="$t('comisiones.grid.consumo_validacion')" :reevaluate="true"/>
    </DxColumn>
    <template #importeTemplate="{ data }">
      <span>{{ $n(data.value) }} €</span>
    </template>
    <template #booleanTemplate="{ data }">
      <BooleanTemplate :value="data.value" />
    </template>
    <template #potenciaTemplate="{ data }">
      <span v-if="data.value">{{ $n(data.value) }} kW</span>
    </template>
    <template #consumoTemplate="{ data }">
      <span v-if="data.value">{{ $n(data.value) }} kWh</span>
    </template>
  </DxDataGrid>

</template>

<script setup lang="ts">
import { DxDataGrid, DxColumn, DxLookup, DxColumnFixing, DxRequiredRule, DxRangeRule, DxCustomRule } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getColumnLookupBoolean, getLookup } from "@helpers/grid-helper";
import { onMounted, ref } from 'vue';
import type { ColumnLookup } from "devextreme/ui/data_grid";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import { useApi } from "@/.shared/.api/integration";
import { type ComisionEnergiaOfertaModel, type EditComisionEnergiaOfertaModel, } from "@/.shared/.api";
import { toastError } from "@/.shared/services/notification-service";
import { useIntl } from "@/.shared/services/i18n";

const gridProperties = ref<DxDataGrid>();
const gridSource = ref<ComisionEnergiaOfertaModel[]>();
const grid = ref<DxDataGrid>();
const api = useApi();
const intl = useIntl();

const props = defineProps({
  idOferta: {
    type: String,
    required: true
  },
  idMarco: {
    type: String,
    required: true
  },
  isEditMode: {
    type: Boolean,
    required: true
  }

});

onMounted(async () => {
  gridProperties.value = {...getDefaultGridProperties(), 
    ...{ editing: { mode: "row", 
        allowUpdating: true, 
        allowAdding: true, 
        allowDeleting: true } }};

  if (props.isEditMode) {
    var apiFunction = await api.toStandardApiFunction("getComisionesPorMarcoByIdOferta", { showLoading: false });
    gridSource.value = await apiFunction(props.idOferta, props.idMarco);
  } else {
    gridSource.value = [];
  }
});

async function onRowInserted(e: { data: ComisionEnergiaOfertaModel }) {
  if (!props.isEditMode) return;
  
  try {
    var result = await api.callStandardApi("createComisionesEnergiaOferta", { showLoading: false }, props.idOferta, props.idMarco, [e.data]);

    // Actualizar directamente el Id en la fila del grid
    e.data.Id = result[0];
  } catch (error) {
    toastError(intl.i18n.t('comisiones.grid.creacion_fallo'))
  }
}

async function onRowUpdated(e: { data: ComisionEnergiaOfertaModel }) {
  if (!props.isEditMode) return;
  
  try {
    if (e.data.Id) {
      await api.callStandardApi("editComisionEnergiaOferta", { showLoading: false }, e.data as EditComisionEnergiaOfertaModel);
    }
  } catch (error) {
    toastError(intl.i18n.t('comisiones.grid.edicion_fallo'))
  }
}

async function onRowRemoved(e: { data: ComisionEnergiaOfertaModel }) {
  if (!props.isEditMode) return;
  
  try {
    await api.callStandardApi("deleteComisionEnergiaOferta", { showLoading: false }, e.data.Id!);
  } catch (error) {
    toastError(intl.i18n.t('comisiones.grid.eliminacion_fallo'))
  }
}

function getLookupTipoTramite(): ColumnLookup {
  return getLookup('comisiones', 'tipo_tramite');
}

function validatePotenciaMin(e: { value: number | null | undefined; data: any }) {
  const potenciaMin = e.value ?? null;
  const potenciaMax = e.data.PotenciaMax ?? null; 

  if (e.data.AplicaPotencia) {
    return potenciaMin !== null || potenciaMax !== null;
  } else {
    return potenciaMin === null;
  }
}

function validatePotenciaMax(e: { value: number | null | undefined; data: any }) {
  const potenciaMax = e.value ?? null; 
  const potenciaMin = e.data.PotenciaMin ?? null; 

  if (e.data.AplicaPotencia) {
    return potenciaMax !== null || potenciaMin !== null;
  } else {
    return potenciaMax === null;
  }
}

function validateConsumoMin(e: { value: number | null | undefined; data: any }) {
  const consumoMin = e.value ?? null;
  const consumoMax = e.data.ConsumoMax ?? null; 

  if (e.data.AplicaConsumo) {
    return consumoMin !== null || consumoMax !== null;
  } else {
    return consumoMin === null;
  }
}

function validateConsumoMax(e: { value: number | null | undefined; data: any }) {
  const consumoMax = e.value ?? null; 
  const consumoMin = e.data.ConsumoMin ?? null; 

  if (e.data.AplicaConsumo) {
    return consumoMax !== null || consumoMin !== null;
  } else {
    return consumoMax === null;
  }
}

defineExpose({
  getComisiones: () => {
    return gridSource.value;
  },
  idMarco: props.idMarco
});

</script>